import { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CATEGORY_LABELS, CATEGORY_ORDER, type ProgramCategory } from '@/data/hooks/usePrograms';

interface Props {
  onUploaded?: () => void;
  trigger?: React.ReactNode;
}

export function UploadProgramDialog({ onUploaded, trigger }: Props) {
  const { mode, userId, dealershipId } = useAuth();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<ProgramCategory>('incentives');
  const [effective, setEffective] = useState('');
  const [expiry, setExpiry] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFile(null); setTitle(''); setCategory('incentives');
    setEffective(''); setExpiry(''); setSubmitting(false);
  };

  const submit = async () => {
    if (mode === 'demo') {
      toast.info('Demo mode — uploads not saved. Sign in to upload real PDFs.');
      setOpen(false);
      return;
    }
    if (!file || !dealershipId || !userId) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (25MB max).');
      return;
    }

    setSubmitting(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${dealershipId}/${Date.now()}-${safeName}`;
      const { error: upErr } = await supabase.storage
        .from('program-files')
        .upload(path, file, { contentType: 'application/pdf' });
      if (upErr) throw upErr;

      const { data: prog, error: insErr } = await supabase
        .from('programs')
        .insert({
          dealership_id: dealershipId,
          uploaded_by: userId,
          title: title.trim() || `Untitled — ${file.name}`,
          category,
          effective_date: effective || null,
          expiry_date: expiry || null,
          file_path: path,
          file_size: file.size,
          processing_status: 'pending',
        })
        .select()
        .single();
      if (insErr) throw insErr;

      toast.success('Uploaded — generating AI summary…');
      setOpen(false);
      reset();
      onUploaded?.();

      // Fire-and-forget AI processing
      supabase.functions.invoke('process-program-pdf', {
        body: { program_id: prog.id },
      }).then(({ error }) => {
        if (error) {
          toast.error('AI processing failed. PDF is uploaded; you can edit details.');
        } else {
          toast.success('AI summary ready');
          onUploaded?.();
        }
      });
    } catch (e: any) {
      console.error(e);
      toast.error(e.message ?? 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button className="gap-2"><Upload className="h-4 w-4" />Upload PDF</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload program PDF</DialogTitle>
          <DialogDescription>
            We'll extract text and generate an AI summary so reps can search it instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>PDF file</Label>
            <Input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} />
          </div>
          <div className="space-y-1.5">
            <Label>Title (optional)</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Auto-detected if blank" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as ProgramCategory)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_ORDER.map(c => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Effective</Label>
              <Input type="date" value={effective} onChange={e => setEffective(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expires</Label>
              <Input type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!file || submitting} className="gap-2">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}